<template>
  <div class="rid-banche">
    <Loader v-if="loader" />

    <!-- Intestazione -->
    <header class="rb-header">
      <div class="rb-header-main">
        <div class="rb-header-icon">
          <i class="pi pi-credit-card"></i>
        </div>
        <div>
          <h1 class="rb-title">RID SDD Banche</h1>
          <p class="rb-subtitle">Firma e invio dei flussi SDD per MPS e Banco BPM</p>
        </div>
      </div>

      <!-- Motivazioni degli storni dal PDF degli insoluti: finestre in RidSddBancheInsoluti -->
      <div class="rb-header-azioni">
        <Button label="Carica insoluti" icon="pi pi-upload" class="rb-btn rb-header-btn"
          @click="$refs.insoluti.apriCaricamento()" />
        <Button label="Storico insoluti" icon="pi pi-list" class="rb-btn rb-header-btn"
          @click="$refs.insoluti.apriStorico()" />
      </div>

      <div class="rb-sync"
        :class="{ 'rb-sync--attiva': sincronizzando, 'rb-sync--ko': !sincronizzando && syncErrore }">
        <i :class="iconaSync"></i>
        <span v-if="sincronizzando">Aggiornamento in corso…</span>
        <span v-else-if="syncErrore" :title="syncErrore">Sincronizzazione non riuscita</span>
        <span v-else-if="ultimoScan">Sincronizzato {{ daQuando(ultimoScan) }}</span>
        <span v-else>In attesa della prima sincronizzazione</span>
      </div>
    </header>

    <div class="rb-body">

      <!-- Selettore banca -->
      <section class="rb-banche">
        <span class="rb-banche-label">Banca</span>
        <SelectButton v-model="bancaSelezionata" :options="opzioniBanca" optionLabel="label" optionValue="value"
          :allowEmpty="false" class="rb-banche-toggle" @change="onCambioBanca" />
      </section>

      <!-- Filtri -->
      <section class="rb-filtri">
        <div class="rb-filtri-campi">
          <div class="rb-campo">
            <label class="rb-label">Stato</label>
            <Dropdown v-model="filtri.stato" :options="opzioniStato" optionLabel="label" optionValue="value"
              placeholder="Tutti" class="w-full" @change="caricaFile" />
          </div>
          <div class="rb-campo">
            <label class="rb-label">Tipo sequenza</label>
            <Dropdown v-model="filtri.sequence_type" :options="opzioniTipo" optionLabel="label" optionValue="value"
              placeholder="Tutti" class="w-full" @change="caricaFile" />
          </div>
          <div class="rb-campo rb-campo--periodo">
            <label class="rb-label">Periodo di rilevamento</label>
            <div class="rb-periodo">
              <Calendar v-model="filtri.dataFrom" dateFormat="dd/mm/yy" :showIcon="true" placeholder="Da" />
              <span class="rb-periodo-sep">—</span>
              <Calendar v-model="filtri.dataTo" dateFormat="dd/mm/yy" :showIcon="true" placeholder="A" />
            </div>
          </div>
          <div class="rb-campo rb-campo--cerca">
            <label class="rb-label">Cerca</label>
            <div class="p-inputgroup">
              <span class="p-inputgroup-addon"><i class="pi pi-search"></i></span>
              <InputText v-model="filtri.search" placeholder="Nome file…" @keyup.enter="caricaFile" />
            </div>
          </div>
        </div>

        <div class="rb-filtri-piede">
          <div class="rb-riepilogo">
            <span class="rb-riepilogo-voce">
              <span class="rb-riepilogo-num">{{ totale }}</span>
              <span class="rb-riepilogo-lab">file</span>
            </span>
            <span class="rb-riepilogo-sep"></span>
            <span class="rb-riepilogo-voce">
              <span class="rb-riepilogo-num">{{ formatCurrency(totaleImporto) }}</span>
              <span class="rb-riepilogo-lab">importo totale</span>
            </span>
          </div>
          <div class="rb-filtri-azioni">
            <Button label="Azzera filtri" icon="pi pi-refresh" class="p-button-outlined p-button-secondary rb-btn"
              @click="azzeraFiltri" />
            <Button label="Applica" icon="pi pi-search" class="rb-btn" @click="caricaFile" />
          </div>
        </div>
      </section>

      <!-- Tabella -->
      <section class="rb-tabella">
        <DataTable :value="files" :paginator="true" :rows="perPagina" :rowsPerPageOptions="[10, 25, 50, 100]"
          dataKey="id" responsiveLayout="stack" breakpoint="1100px" :loading="loading" :lazy="true"
          :totalRecords="totale" @page="onPagina"
          paginatorTemplate="CurrentPageReport FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink RowsPerPageDropdown"
          currentPageReportTemplate="Mostra {first} a {last} di {totalRecords}" class="p-datatable-sm rb-dt">

          <template #empty>
            <div class="rb-vuoto">
              <i class="pi pi-folder-open"></i>
              <p class="rb-vuoto-titolo">Nessun file trovato</p>
              <p class="rb-vuoto-testo">
                I file compaiono qui appena vengono rilevati.
              </p>
            </div>
          </template>

          <Column field="banca" header="Banca" style="width: 7rem">
            <template #body="{ data }">
              <span class="p-column-title">Banca</span>
              <span class="rb-banca" :class="'rb-banca--' + data.banca.toLowerCase()">
                {{ data.banca === 'SCONOSCIUTA' ? 'Da attribuire' : data.banca }}
              </span>
            </template>
          </Column>

          <Column field="filename" header="File" style="min-width: 16rem">
            <template #body="{ data }">
              <span class="p-column-title">File</span>
              <div class="rb-file">
                <i class="pi pi-file rb-file-icona"></i>
                <div class="rb-file-testo">
                  <div class="rb-file-nome">
                    {{ data.filename }}
                    <i v-if="!data.parseOk" class="pi pi-exclamation-triangle rb-avviso"
                      v-tooltip.top="'File non leggibile: ' + (data.parseErrore || 'errore di parsing')"></i>
                  </div>
                </div>
              </div>
            </template>
          </Column>

          <Column field="sequenceType" header="Tipo" style="width: 7rem">
            <template #body="{ data }">
              <span class="p-column-title">Tipo</span>
              <div class="rb-tipi">
                <span v-if="data.sequenceType" class="rb-tipo" :class="'rb-tipo--' + data.sequenceType.toLowerCase()"
                  v-tooltip.top="data.sequenceType === 'FRST' ? 'Primo addebito del mandato' : 'Addebito ricorrente'">
                  {{ data.sequenceType }}
                </span>
                <span v-else class="rb-vuoto-cella">—</span>
              </div>
            </template>
          </Column>

          <Column field="dataIncasso" header="Incasso" style="width: 7rem">
            <template #body="{ data }">
              <span class="p-column-title">Incasso</span>
              <span v-if="data.dataIncasso" class="rb-incasso">{{ formatDate(data.dataIncasso) }}</span>
              <span v-else class="rb-vuoto-cella">—</span>
            </template>
          </Column>

          <Column field="importo" header="Importo" style="width: 9rem">
            <template #body="{ data }">
              <span class="p-column-title">Importo</span>
              <div class="rb-cella-multi">
                <span v-if="data.importo !== null" class="rb-importo">{{ formatCurrency(data.importo) }}</span>
                <span v-else class="rb-vuoto-cella">—</span>
                <span v-if="data.numTransazioni !== null" class="rb-sotto">
                  {{ data.numTransazioni }} {{ data.numTransazioni === 1 ? 'transazione' : 'transazioni' }}
                </span>
              </div>
            </template>
          </Column>

          <Column field="stato" header="Stato" style="width: 9.5rem">
            <template #body="{ data }">
              <span class="p-column-title">Stato</span>
              <span class="rb-stato" :class="'rb-stato--' + data.stato.toLowerCase()">
                <span class="rb-stato-pallino"></span>
                {{ etichettaStato(data.stato) }}
              </span>
              <i v-if="data.inTransizione" class="pi pi-spin pi-spinner rb-transizione"
                v-tooltip.top="'Transizione in corso'"></i>
            </template>
          </Column>

          <Column header="Azioni" style="width: 12rem" bodyClass="rb-cella-azioni">
            <template #body="{ data }">
              <span class="p-column-title">Azioni</span>
              <div class="rb-azioni">
                <Button v-if="data.azioni.includes('scarica')" icon="pi pi-download" class="rb-azione rb-azione--ok"
                  v-tooltip.top="'Scarica'" :loading="inCorso === data.id"
                  @click="scarica(data)" />

                <Button v-if="data.stato === 'IN_LAVORAZIONE'" icon="pi pi-download" class="rb-azione"
                  v-tooltip.top="'Scarica di nuovo'" :loading="inCorso === data.id"
                  @click="riscarica(data)" />

                <Button v-if="data.azioni.includes('annulla')" icon="pi pi-arrow-left" class="rb-azione"
                  v-tooltip.top="'Rimetti in attesa'" :loading="inCorso === data.id"
                  @click="chiediConferma('annulla', data)" />

                <Button v-if="data.azioni.includes('firmato')" icon="pi pi-check-circle" class="rb-azione rb-azione--ok"
                  v-tooltip.top="'Segna come firmato'" :loading="inCorso === data.id"
                  @click="chiediConferma('firmato', data)" />

                <Button v-if="data.azioni.includes('rifiutato')" icon="pi pi-trash" class="rb-azione rb-azione--ko"
                  v-tooltip.top="'Segna come rifiutato'" :loading="inCorso === data.id"
                  @click="chiediConferma('rifiutato', data)" />

                <Button v-if="data.stato === 'FIRMATO'" icon="pi pi-euro" class="rb-azione rb-azione--ok"
                  v-tooltip.top="'Verifica pagamenti e mandati registrati'" :loading="inCorso === data.id"
                  @click="apriVerifica(data)" />
              </div>
            </template>
          </Column>
        </DataTable>
      </section>
    </div>

    <!-- Conferma azione -->
    <Dialog v-model:visible="mostraConferma" :header="titoloConferma" :modal="true" :style="{ width: '30rem' }"
      :closable="false" class="rb-dialog">
      <div class="rb-conferma">
        <div class="rb-conferma-icona" :class="'rb-conferma-icona--' + (azioneConferma || 'default')">
          <i :class="iconaConferma"></i>
        </div>
        <div class="rb-conferma-testo">
          <p class="rb-conferma-msg">{{ messaggioConferma }}</p>
          <div v-if="fileConferma" class="rb-conferma-file">
            <div class="rb-conferma-file-nome">{{ fileConferma.filename }}</div>
            <div class="rb-conferma-file-meta">
              {{ fileConferma.banca }}
              <template v-if="fileConferma.importo !== null"> · {{ formatCurrency(fileConferma.importo) }}</template>
              <template v-if="fileConferma.numTransazioni !== null"> · {{ fileConferma.numTransazioni }} tx</template>
            </div>
          </div>
          <div v-if="attesaConferma" class="rb-attesa">
            <span>Conferma disponibile tra {{ countdown }}s</span>
            <div class="rb-attesa-barra">
              <div class="rb-attesa-avanzamento" :style="{ width: progressoConferma + '%' }"></div>
            </div>
          </div>
        </div>
      </div>
      <template #footer>
        <Button label="Annulla" icon="pi pi-times" class="p-button-text rb-btn" @click="chiudiConferma" />
        <Button label="Conferma" icon="pi pi-check" class="rb-btn" :disabled="attesaConferma"
          @click="confermaAzione" />
      </template>
    </Dialog>

    <!-- Verifica contabile: file originale a confronto con payments e rid -->
    <Dialog v-model:visible="mostraVerifica" :modal="true" :style="{ width: '62rem' }" class="rb-dialog">
      <template #header>
        <div class="rb-vh">
          <span class="rb-vh-titolo">Verifica contabile</span>
          <span v-if="fileVerifica" class="rb-vh-file">{{ fileVerifica.filename }}</span>
        </div>
      </template>

      <div v-if="caricandoVerifica" class="rb-caricamento">
        <i class="pi pi-spin pi-spinner"></i>
      </div>

      <div v-else-if="verifica">
        <div class="rb-esito" :class="verifica.completo ? 'rb-esito--ok' : 'rb-esito--ko'">
          <i :class="verifica.completo ? 'pi pi-check-circle' : 'pi pi-exclamation-triangle'"></i>
          <div>
            <div class="rb-esito-titolo">
              <template v-if="!verifica.disponibile">Contabilità non consultabile</template>
              <template v-else-if="verifica.completo">Tutto registrato</template>
              <template v-else>Registrazione incompleta</template>
            </div>
            <div class="rb-esito-testo">
              <template v-if="!verifica.disponibile">
                Le tabelle payments e rid non sono raggiungibili su questa connessione.
              </template>
              <template v-else-if="verifica.completo">
                Tutte le {{ verifica.totale_transazioni }} disposizioni del file risultano registrate,
                pagamento e mandato.
              </template>
              <template v-else>
                {{ verifica.totale_transazioni - verifica.pagamenti_registrati }} disposizioni su
                {{ verifica.totale_transazioni }} non risultano registrate.
              </template>
            </div>
          </div>
        </div>

        <div class="rb-confronto">
          <div class="rb-conf-voce">
            <span class="rb-conf-lab">Disposizioni nel file</span>
            <span class="rb-conf-num">{{ verifica.totale_transazioni }}</span>
          </div>
          <div class="rb-conf-voce">
            <span class="rb-conf-lab">Pagamenti registrati</span>
            <span class="rb-conf-num" :class="verifica.pagamenti_registrati === verifica.totale_transazioni ? 'rb-conf-ok' : 'rb-conf-ko'">{{ verifica.pagamenti_registrati }}</span>
          </div>
          <div class="rb-conf-voce">
            <span class="rb-conf-lab">Mandati registrati</span>
            <span class="rb-conf-num" :class="verifica.rid_registrati === verifica.totale_transazioni ? 'rb-conf-ok' : 'rb-conf-ko'">{{ verifica.rid_registrati }}</span>
          </div>
          <div class="rb-conf-sep"></div>
          <div class="rb-conf-voce">
            <span class="rb-conf-lab">Importo del file</span>
            <span class="rb-conf-num">{{ formatCurrency(verifica.totale_importo) }}</span>
          </div>
          <div class="rb-conf-voce">
            <span class="rb-conf-lab">Importo registrato</span>
            <span class="rb-conf-num" :class="verifica.importo_registrato === verifica.totale_importo ? 'rb-conf-ok' : 'rb-conf-ko'">{{ formatCurrency(verifica.importo_registrato) }}</span>
          </div>
          <div v-if="verifica.importo_registrato !== verifica.totale_importo" class="rb-conf-voce">
            <span class="rb-conf-lab">Differenza</span>
            <span class="rb-conf-num rb-conf-ko">{{ formatCurrency(verifica.totale_importo - verifica.importo_registrato) }}</span>
          </div>
        </div>

        <div class="rb-verifica-barra">
          <span class="rb-verifica-conteggio">
            {{ righeVerifica.length }} di {{ verifica.righe.length }} disposizioni mostrate
          </span>
          <div class="rb-verifica-toggle">
            <Checkbox v-model="soloNonRegistrate" :binary="true" inputId="soloKo" />
            <label for="soloKo">Solo quelle non registrate</label>
          </div>
        </div>

        <DataTable :value="righeVerifica" :paginator="righeVerifica.length > 12" :rows="12"
          responsiveLayout="scroll" stripedRows class="p-datatable-sm rb-dt rb-dt-verifica">
          <template #empty>
            <div class="rb-vuoto"><p class="rb-vuoto-testo">Nessuna disposizione da mostrare</p></div>
          </template>

          <Column field="fattura" header="Fattura" style="width: 8rem">
            <template #body="{ data }">
              <span v-if="data.fattura" class="rb-mono">{{ data.fattura }}</span>
              <span v-else class="rb-vuoto-cella">non estraibile</span>
            </template>
          </Column>

          <Column field="intestatario" header="Intestatario" style="min-width: 11rem">
            <template #body="{ data }">
              <span v-if="data.intestatario">{{ data.intestatario }}</span>
              <span v-else class="rb-vuoto-cella">—</span>
            </template>
          </Column>

          <Column field="importo" header="Importo" style="width: 7.5rem">
            <template #body="{ data }">
              <span class="rb-importo">{{ formatCurrency(data.importo) }}</span>
            </template>
          </Column>

          <!--
            Il motivo sta sulla singola casella, non solo nella colonna Motivo:
            quando manca una sola delle due il messaggio complessivo non dice
            quale cercare. Passando il cursore sulla X si legge il perche' di
            quella X, con i criteri esatti usati per cercarla.
          -->
          <Column header="Pagamento" style="width: 6.5rem">
            <template #body="{ data }">
              <span class="rb-spunta" :class="[
                data.pagamento ? 'rb-spunta--ok' : 'rb-spunta--ko',
                data.nota_pagamento ? 'rb-spunta--motivo' : '',
              ]" v-tooltip.top="data.nota_pagamento">
                <i :class="data.pagamento ? 'pi pi-check' : 'pi pi-times'"></i>
              </span>
            </template>
          </Column>

          <Column header="Mandato" style="width: 6.5rem">
            <template #body="{ data }">
              <span class="rb-spunta" :class="[
                data.rid ? 'rb-spunta--ok' : 'rb-spunta--ko',
                data.nota_rid ? 'rb-spunta--motivo' : '',
              ]" v-tooltip.top="data.nota_rid">
                <i :class="data.rid ? 'pi pi-check' : 'pi pi-times'"></i>
              </span>
            </template>
          </Column>

          <Column field="nota" header="Motivo" style="min-width: 13rem">
            <template #body="{ data }">
              <span v-if="data.nota" class="rb-nota">{{ data.nota }}</span>
              <span v-else class="rb-vuoto-cella">—</span>
            </template>
          </Column>
        </DataTable>
      </div>

      <template #footer>
        <Button label="Riverifica" icon="pi pi-refresh" class="p-button-outlined p-button-secondary rb-btn"
          :loading="caricandoVerifica" @click="apriVerifica(fileVerifica)" />
        <Button label="Chiudi" icon="pi pi-times" class="p-button-text rb-btn" @click="mostraVerifica = false" />
      </template>
    </Dialog>

    <!-- Import delle motivazioni degli storni dal PDF degli insoluti -->
    <RidSddBancheInsoluti ref="insoluti" />
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, onBeforeUnmount, getCurrentInstance } from "vue";
import Loader from "@/components/Loader.vue";
import moment from "moment";

import DataTable from "primevue/datatable";
import Column from "primevue/column";
import Button from "primevue/button";
import InputText from "primevue/inputtext";
import Dropdown from "primevue/dropdown";
import Calendar from "primevue/calendar";
import Dialog from "primevue/dialog";
import SelectButton from "primevue/selectbutton";
import Checkbox from "primevue/checkbox";

import RidSddBancheService from "@/services/ask-pratiche/rid-sdd-banche-service";
import RidSddBancheInsoluti from "@/components/pratiche/RidSddBancheInsoluti.vue";

const ETICHETTE_STATO = {
  IN_ATTESA: "In attesa",
  IN_LAVORAZIONE: "In lavorazione",
  FIRMATO: "Firmato",
  RIFIUTATO: "Rifiutato",
  ASSENTE: "Assente",
  CONFLITTO: "Conflitto",
};

/** Azioni per cui si richiede un'attesa prima di poter confermare. */
const AZIONI_CON_ATTESA = ["firmato", "rifiutato"];

const TESTI_AZIONE = {
  annulla: {
    titolo: "Rimettere il file in attesa?",
    messaggio: "Il file tornerà disponibile per tutti.",
    icona: "pi pi-arrow-left",
  },
  firmato: {
    titolo: "Confermare la firma?",
    messaggio: "Conferma solo dopo aver caricato il file sul portale della banca.",
    icona: "pi pi-check-circle",
  },
  rifiutato: {
    titolo: "Segnalare il file come rifiutato?",
    messaggio: "Il file verrà segnato come rifiutato.",
    icona: "pi pi-trash",
  },
};

/** Intervallo della sincronizzazione automatica con l'FTP. */
const INTERVALLO_SYNC_MS = 120000;

export default {
  name: "RidSddBanche",
  components: {
    Loader, DataTable, Column, Button, InputText, Dropdown, Calendar, Dialog, SelectButton, Checkbox,
    RidSddBancheInsoluti,
  },

  setup() {
    const { proxy } = getCurrentInstance();
    const service = new RidSddBancheService(proxy.$http, proxy.$eventBus);

    const loader = ref(false);
    const loading = ref(false);
    const sincronizzando = ref(false);
    // Motivo dell'ultima sincronizzazione fallita: senza, un FTP irraggiungibile
    // lascerebbe l'indicatore a girare all'infinito senza dire niente.
    const syncErrore = ref(null);

    const iconaSync = computed(() => {
      if (sincronizzando.value) return "pi pi-spin pi-spinner";
      if (syncErrore.value) return "pi pi-exclamation-triangle";
      return "pi pi-cloud-download";
    });
    const inCorso = ref(null);

    const files = ref([]);
    const totale = ref(0);
    const totaleImporto = ref(0);
    const pagina = ref(1);
    const perPagina = ref(25);
    const ultimoScan = ref(null);

    const bancaSelezionata = ref("tutte");
    const opzioniBanca = [
      { label: "Tutte", value: "tutte" },
      { label: "MPS", value: "MPS" },
      { label: "BPM", value: "BPM" },
      { label: "Da attribuire", value: "SCONOSCIUTA" },
    ];

    const opzioniStato = [
      { label: "Tutti", value: "tutti" },
      { label: "In attesa", value: "IN_ATTESA" },
      { label: "In lavorazione", value: "IN_LAVORAZIONE" },
      { label: "Firmato", value: "FIRMATO" },
      { label: "Rifiutato", value: "RIFIUTATO" },
      { label: "Conflitto", value: "CONFLITTO" },
      { label: "Assente", value: "ASSENTE" },
    ];

    const opzioniTipo = [
      { label: "Tutti", value: "tutti" },
      { label: "FRST", value: "FRST" },
      { label: "RCUR", value: "RCUR" },
      { label: "MIXED", value: "MIXED" },
    ];

    const filtri = reactive({
      stato: "tutti",
      sequence_type: "tutti",
      dataFrom: null,
      dataTo: null,
      search: "",
    });

    // ── Caricamento ───────────────────────────────────────────────────

    const filtriPerApi = () => {
      const f = {
        stato: filtri.stato,
        sequence_type: filtri.sequence_type,
        search: filtri.search || null,
        dataFrom: filtri.dataFrom ? moment(filtri.dataFrom).format("YYYY-MM-DD") : null,
        dataTo: filtri.dataTo ? moment(filtri.dataTo).format("YYYY-MM-DD") : null,
      };

      if (bancaSelezionata.value === "SCONOSCIUTA") {
        f.solo_da_attribuire = true;
      } else if (bancaSelezionata.value !== "tutte") {
        f.banca = bancaSelezionata.value;
      }

      return f;
    };

    const caricaFile = async () => {
      loading.value = true;
      try {
        const r = await service.getFiles(filtriPerApi(), pagina.value, perPagina.value);
        files.value = r.items;
        totale.value = r.total;
        totaleImporto.value = r.totaleImporto;
        ultimoScan.value = r.ultimoScan;
      } finally {
        loading.value = false;
      }
    };

    const onPagina = (e) => {
      pagina.value = e.page + 1;
      perPagina.value = e.rows;
      caricaFile();
    };

    const onCambioBanca = () => {
      pagina.value = 1;
      caricaFile();
    };

    const azzeraFiltri = () => {
      filtri.stato = "tutti";
      filtri.sequence_type = "tutti";
      filtri.dataFrom = null;
      filtri.dataTo = null;
      filtri.search = "";
      pagina.value = 1;
      caricaFile();
    };

    // ── Sincronizzazione automatica ───────────────────────────────────

    let timerSync = null;

    /**
     * Rilegge l'FTP e riallinea, poi ricarica l'elenco.
     *
     * Non blocca mai la pagina: l'elenco viene già dal database, la
     * sincronizzazione gli si sovrappone. Nessun avviso quando non è cambiato
     * nulla — un'operazione che gira da sola ogni due minuti non deve
     * riempire lo schermo di notifiche.
     */
    const sincronizza = async ({ silenzioso = true } = {}) => {
      if (sincronizzando.value) return;

      sincronizzando.value = true;
      try {
        const r = await service.scan();
        const s = r.risultato || {};

        syncErrore.value = null;

        // Un'altra sessione stava già sincronizzando: nulla da segnalare.
        if (s.saltato) return;

        const cambiamenti = (s.nuovi || 0) + (s.riallineati || 0) + (s.assenti || 0) + (s.riparati || 0);

        if (cambiamenti > 0) {
          proxy.$toast.add({
            severity: "info",
            summary: "Elenco aggiornato",
            detail: descriviCambiamenti(s),
            life: 5000,
          });
        }

        await caricaFile();
      } catch (err) {
        syncErrore.value = messaggioErrore(err);

        // Un fallimento della sincronizzazione automatica non deve
        // interrompere il lavoro: si segnala solo se l'utente la sta
        // aspettando, altrimenti resta nell'indicatore in testata.
        if (!silenzioso) {
          proxy.$toast.add({
            severity: "error",
            summary: "Sincronizzazione non riuscita",
            detail: messaggioErrore(err),
            life: 6000,
          });
        } else {
          console.error("Sincronizzazione FTP non riuscita:", err);
        }
      } finally {
        sincronizzando.value = false;
      }
    };

    const descriviCambiamenti = (s) => {
      const parti = [];
      if (s.nuovi) parti.push(`${s.nuovi} nuov${s.nuovi === 1 ? "o" : "i"}`);
      if (s.riallineati) parti.push(`${s.riallineati} riallineat${s.riallineati === 1 ? "o" : "i"}`);
      if (s.riparati) parti.push(`${s.riparati} riparat${s.riparati === 1 ? "o" : "i"}`);
      if (s.assenti) parti.push(`${s.assenti} non più disponibili`);
      return parti.join(" · ");
    };

    const avviaSyncAutomatica = () => {
      clearInterval(timerSync);
      timerSync = setInterval(() => {
        // Niente chiamate all'FTP mentre la scheda è in secondo piano.
        if (document.visibilityState === "visible") sincronizza();
      }, INTERVALLO_SYNC_MS);
    };

    /** Tornando sulla scheda si riallinea subito, senza aspettare il timer. */
    const onVisibilita = () => {
      if (document.visibilityState === "visible") sincronizza();
    };

    // ── Download ──────────────────────────────────────────────────────

    /**
     * Prende in carico e scarica. Se la presa in carico fallisce non parte
     * nessun download: il file resta in Inbox.
     */
    const scarica = async (file) => {
      inCorso.value = file.id;
      try {
        await service.scarica(file.id, file.filename);
        proxy.$toast.add({
          severity: "success",
          summary: "File scaricato",
          detail: `${file.filename} è ora in lavorazione. Caricalo sul portale della banca, poi segna l'esito.`,
          life: 6000,
        });
      } catch (err) {
        gestisciErroreAzione(err, "Download non riuscito");
      } finally {
        inCorso.value = null;
        await caricaFile();
      }
    };

    /** Riscarica un file già in lavorazione, senza toccarne lo stato. */
    const riscarica = async (file) => {
      inCorso.value = file.id;
      try {
        await service.scaricaContenuto(file.id, file.filename);
      } catch (err) {
        gestisciErroreAzione(err, "Download non riuscito");
      } finally {
        inCorso.value = null;
      }
    };

    // ── Conferma azioni ───────────────────────────────────────────────

    const mostraConferma = ref(false);
    const azioneConferma = ref(null);
    const fileConferma = ref(null);
    const attesaConferma = ref(false);
    const countdown = ref(0);
    const ATTESA_SECONDI = 5;
    let timerConferma = null;

    const titoloConferma = ref("");
    const messaggioConferma = ref("");
    const iconaConferma = ref("");
    const progressoConferma = ref(0);

    const chiediConferma = (azione, file) => {
      const testi = TESTI_AZIONE[azione];
      azioneConferma.value = azione;
      fileConferma.value = file;
      titoloConferma.value = testi.titolo;
      messaggioConferma.value = testi.messaggio;
      iconaConferma.value = testi.icona;
      mostraConferma.value = true;

      if (AZIONI_CON_ATTESA.includes(azione)) {
        avviaAttesa();
      } else {
        attesaConferma.value = false;
      }
    };

    const avviaAttesa = () => {
      attesaConferma.value = true;
      countdown.value = ATTESA_SECONDI;
      progressoConferma.value = 0;
      clearInterval(timerConferma);
      timerConferma = setInterval(() => {
        countdown.value -= 1;
        progressoConferma.value = ((ATTESA_SECONDI - countdown.value) / ATTESA_SECONDI) * 100;
        if (countdown.value <= 0) {
          clearInterval(timerConferma);
          attesaConferma.value = false;
        }
      }, 1000);
    };

    const chiudiConferma = () => {
      clearInterval(timerConferma);
      mostraConferma.value = false;
      azioneConferma.value = null;
      fileConferma.value = null;
      attesaConferma.value = false;
    };

    const confermaAzione = async () => {
      const azione = azioneConferma.value;
      const file = fileConferma.value;
      chiudiConferma();

      if (!azione || !file) return;

      inCorso.value = file.id;
      try {
        const r = await service.azione(azione, file.id);
        proxy.$toast.add({
          severity: "success",
          summary: "Operazione completata",
          detail: r.message || `${file.filename}: ${etichettaStato(r.stato)}`,
          life: 4000,
        });
      } catch (err) {
        gestisciErroreAzione(err, "Operazione non riuscita");
      } finally {
        inCorso.value = null;
        await caricaFile();
      }

      // Dopo una firma la verifica si apre da sola: è il momento in cui
      // l'operatore deve sapere se la contabilità è andata a posto, non
      // qualcosa da ricordarsi di controllare dopo.
      if (azione === "firmato") {
        apriVerifica(file);
      }
    };

    // ── Errori ────────────────────────────────────────────────────────

    const messaggioErrore = (err) => {
      return (err && err.response && err.response.data && err.response.data.message)
        || (err && err.message)
        || "Errore imprevisto";
    };

    /**
     * Il 409 non è un guasto ma una collisione prevista: un altro operatore è
     * arrivato prima, o lo stato è cambiato. Va mostrato come avviso con il
     * messaggio del backend, non come errore rosso generico.
     */
    const gestisciErroreAzione = (err, riepilogo) => {
      const stato = err && err.response ? err.response.status : null;
      proxy.$toast.add({
        severity: stato === 409 || stato === 403 ? "warn" : "error",
        summary: stato === 409 ? "Operazione non possibile" : riepilogo,
        detail: messaggioErrore(err),
        life: 7000,
      });
    };

    // ── Verifica contabile ────────────────────────────────────────────

    const mostraVerifica = ref(false);
    const caricandoVerifica = ref(false);
    const verifica = ref(null);
    const fileVerifica = ref(null);
    const soloNonRegistrate = ref(false);

    /**
     * Con 67 disposizioni l'elenco completo è illeggibile quando qualcosa non
     * torna: chi apre la verifica cerca le righe con un problema, non le altre.
     */
    const righeVerifica = computed(() => {
      if (!verifica.value) return [];
      const righe = verifica.value.righe || [];
      return soloNonRegistrate.value
        ? righe.filter((r) => !r.pagamento || !r.rid)
        : righe;
    });

    const apriVerifica = async (file) => {
      if (!file) return;

      fileVerifica.value = file;
      mostraVerifica.value = true;
      caricandoVerifica.value = true;
      verifica.value = null;

      try {
        const r = await service.verificaPagamenti(file.id);
        verifica.value = r.verifica;
        // Se c'è qualcosa fuori posto si parte già filtrati sui problemi.
        soloNonRegistrate.value = !r.verifica.completo;
      } catch (err) {
        mostraVerifica.value = false;
        proxy.$toast.add({
          severity: "error",
          summary: "Verifica non riuscita",
          detail: messaggioErrore(err),
          life: 6000,
        });
      } finally {
        caricandoVerifica.value = false;
      }
    };

    // ── Formattazione ─────────────────────────────────────────────────

    const formatCurrency = (v) => {
      if (v === null || v === undefined) return "—";
      return new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v);
    };

    const formatDateTime = (v) => (v ? moment(v).format("DD/MM/YYYY HH:mm") : "—");

    const formatDate = (v) => (v ? moment(v).format("DD/MM/YYYY") : "—");

    /** Tempo trascorso in italiano, senza dipendere dalla locale di moment. */
    const daQuando = (v) => {
      if (!v) return "—";

      const secondi = Math.max(0, Math.floor((Date.now() - new Date(v).getTime()) / 1000));

      if (secondi < 60) return "poco fa";
      if (secondi < 3600) {
        const m = Math.floor(secondi / 60);
        return m === 1 ? "1 minuto fa" : `${m} minuti fa`;
      }
      if (secondi < 86400) {
        const h = Math.floor(secondi / 3600);
        return h === 1 ? "1 ora fa" : `${h} ore fa`;
      }
      const g = Math.floor(secondi / 86400);
      if (g <= 7) return g === 1 ? "ieri" : `${g} giorni fa`;

      return moment(v).format("DD/MM/YYYY");
    };

    const etichettaStato = (s) => ETICHETTE_STATO[s] || s || "—";

    onMounted(async () => {
      // L'elenco arriva subito dal database; la sincronizzazione con l'FTP
      // parte dopo e si sovrappone, così la pagina non aspetta mai la rete.
      await caricaFile();
      sincronizza();
      avviaSyncAutomatica();
      document.addEventListener("visibilitychange", onVisibilita);
    });

    onBeforeUnmount(() => {
      clearInterval(timerConferma);
      clearInterval(timerSync);
      document.removeEventListener("visibilitychange", onVisibilita);
    });

    return {
      loader, loading, sincronizzando, syncErrore, iconaSync, inCorso,
      files, totale, totaleImporto, perPagina, ultimoScan,
      bancaSelezionata, opzioniBanca, opzioniStato, opzioniTipo, filtri,
      caricaFile, onPagina, onCambioBanca, azzeraFiltri,
      scarica, riscarica,
      mostraConferma, titoloConferma, messaggioConferma, iconaConferma, fileConferma, azioneConferma,
      attesaConferma, countdown, progressoConferma,
      chiediConferma, chiudiConferma, confermaAzione,
      mostraVerifica, caricandoVerifica, verifica, fileVerifica, soloNonRegistrate,
      righeVerifica, apriVerifica,
      formatCurrency, formatDateTime, formatDate, daQuando, etichettaStato,
    };
  },
};
</script>

<style scoped src="./rid-sdd-banche-comune.css"></style>

<style scoped>
/* ──────────────────────────────────────────────────────────────────────
   Tre soglie:
     1100px  la tabella ha 10 colonne: sotto questa larghezza le righe
             diventano schede impilate, altrimenti resta illeggibile
      900px  tablet: la testata e i filtri si dispongono in colonna
      600px  telefono: tutto a piena larghezza, aree toccabili più grandi
   ────────────────────────────────────────────────────────────────────── */

.rid-banche {
  width: 100%;
  max-width: 100%;
}

/* ── Intestazione ──────────────────────────────────────────────────── */

.rb-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1.25rem;
  flex-wrap: wrap;
  padding: 2rem 2.5rem;
  background: linear-gradient(135deg, var(--primary-color) 0%, var(--primary-700) 100%);
  color: #fff;
}

.rb-header-main {
  display: flex;
  align-items: center;
  gap: 1.15rem;
  min-width: 0;
}

.rb-header-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 3rem;
  height: 3rem;
  border-radius: 12px;
  background: rgba(255, 255, 255, 0.16);
  flex-shrink: 0;
}

.rb-header-icon i {
  font-size: 1.4rem;
}

.rb-title {
  margin: 0;
  font-size: 1.6rem;
  font-weight: 600;
  line-height: 1.2;
  letter-spacing: -0.01em;
}

.rb-subtitle {
  margin: 0.35rem 0 0 0;
  font-size: 0.9rem;
  opacity: 0.85;
}

/* Indicatore di sincronizzazione: non è un comando, è uno stato */
.rb-sync {
  display: inline-flex;
  align-items: center;
  gap: 0.55rem;
  padding: 0.55rem 1.05rem;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.14);
  font-size: 0.85rem;
  line-height: 1;
  white-space: nowrap;
  transition: background 0.25s ease;
}

.rb-sync--attiva {
  background: rgba(255, 255, 255, 0.26);
}

/* Un FTP irraggiungibile deve vedersi, non restare uno spinner eterno */
.rb-sync--ko {
  background: rgba(220, 38, 38, 0.35);
  cursor: help;
}

.rb-sync i {
  font-size: 0.9rem;
}

/* Comandi degli insoluti: sullo sfondo della testata, a destra accanto all'indicatore */
.rb-header-azioni {
  display: flex;
  align-items: center;
  gap: 0.6rem;
  margin-left: auto;
}

.rb-header .rb-header-btn {
  border-radius: 999px;
  border: 1px solid rgba(255, 255, 255, 0.32);
  background: rgba(255, 255, 255, 0.14);
  color: #fff;
}

.rb-header .rb-header-btn:enabled:hover {
  border-color: rgba(255, 255, 255, 0.5);
  background: rgba(255, 255, 255, 0.26);
  color: #fff;
}

.rb-header .rb-header-btn:focus {
  box-shadow: 0 0 0 2px rgba(255, 255, 255, 0.45);
}

@media screen and (max-width: 900px) {
  .rb-header-azioni {
    margin-left: 0;
    width: 100%;
  }

  .rb-header-azioni .rb-header-btn {
    flex: 1;
    justify-content: center;
  }
}

/* ── Corpo ─────────────────────────────────────────────────────────── */

.rb-body {
  padding: 2.25rem 2.5rem 3rem;
  background: var(--surface-ground);
  min-height: calc(100vh - 200px);
}

/* ── Selettore banca ───────────────────────────────────────────────── */

.rb-banche {
  display: flex;
  align-items: center;
  gap: 1rem;
  flex-wrap: wrap;
  margin-bottom: 1.75rem;
}

.rb-banche-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-color-secondary);
}

.rb-banche-toggle {
  display: inline-flex;
  gap: 0.5rem;
  background: transparent;
}

.rb-banche-toggle :deep(.p-button),
.rb-banche-toggle :deep(.p-button:first-child),
.rb-banche-toggle :deep(.p-button:last-child) {
  border-radius: 20px !important;
  min-width: 6.5rem;
  justify-content: center;
  padding: 0.5rem 1.15rem;
  font-size: 0.86rem;
  font-weight: 500;
  border: 1px solid var(--surface-border) !important;
  background: var(--surface-card);
  color: var(--text-color);
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.04);
  transition: all 0.2s ease;
}

.rb-banche-toggle :deep(.p-button:not(.p-highlight):hover) {
  background: var(--surface-hover);
  border-color: var(--primary-color) !important;
  color: var(--primary-color);
}

.rb-banche-toggle :deep(.p-button.p-highlight) {
  background: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  color: #ffffff !important;
  box-shadow: 0 2px 6px rgba(0, 0, 0, 0.15);
}

/* ── Filtri ────────────────────────────────────────────────────────── */

.rb-filtri {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 10px;
  overflow: hidden;
  margin-bottom: 1.75rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

.rb-filtri-campi {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(15rem, 1fr));
  gap: 1.35rem 1.35rem;
  padding: 1.6rem 1.6rem;
}

.rb-campo {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
  min-width: 0;
}

.rb-campo--periodo {
  grid-column: span 2;
}

.rb-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.04em;
  color: var(--text-color-secondary);
}

.rb-periodo {
  display: flex;
  align-items: center;
  gap: 0.6rem;
}

.rb-periodo :deep(.p-calendar) {
  flex: 1 1 0;
  min-width: 0;
}

.rb-periodo-sep {
  color: var(--text-color-secondary);
  flex-shrink: 0;
}

.rb-filtri-piede {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  flex-wrap: wrap;
  padding: 1rem 1.6rem;
  border-top: 1px solid var(--surface-border);
  background: var(--surface-ground);
}

.rb-riepilogo {
  display: flex;
  align-items: center;
  gap: 1.15rem;
}

.rb-riepilogo-voce {
  display: flex;
  align-items: baseline;
  gap: 0.4rem;
}

.rb-riepilogo-num {
  font-size: 1.05rem;
  font-weight: 600;
  color: var(--text-color);
}

.rb-riepilogo-lab {
  font-size: 0.82rem;
  color: var(--text-color-secondary);
}

.rb-riepilogo-sep {
  width: 1px;
  height: 1.15rem;
  background: var(--surface-border);
}

.rb-filtri-azioni {
  display: flex;
  gap: 0.6rem;
}

/* ── Tabella ───────────────────────────────────────────────────────── */

.rb-tabella {
  background: var(--surface-card);
  border: 1px solid var(--surface-border);
  border-radius: 10px;
  overflow: hidden;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
}

/* Banca */

.rb-banca--sconosciuta { background: rgba(245, 158, 11, 0.16); color: #b45309; }

/* File */

.rb-file-testo { min-width: 0; }

.rb-avviso {
  margin-left: 0.35rem;
  color: #d97706;
  font-size: 0.85rem;
}

/* Tipo e schema */
.rb-tipi {
  display: flex;
  align-items: center;
  gap: 0.3rem;
  flex-wrap: wrap;
}

.rb-tipo {
  display: inline-block;
  padding: 0.18rem 0.5rem;
  border-radius: 5px;
  font-size: 0.72rem;
  font-weight: 600;
  letter-spacing: 0.03em;
}

.rb-tipo--frst { background: rgba(99, 102, 241, 0.14); color: #4338ca; }
.rb-tipo--rcur { background: rgba(100, 116, 139, 0.14); color: #475569; }
.rb-tipo--mixed { background: rgba(217, 119, 6, 0.14); color: #b45309; }

.rb-schema {
  display: inline-block;
  padding: 0.18rem 0.45rem;
  border-radius: 5px;
  font-size: 0.7rem;
  font-weight: 600;
  letter-spacing: 0.03em;
  border: 1px solid transparent;
}

/* B2B non ha diritto di storno: va distinto a colpo d'occhio dal CORE */
.rb-schema--b2b { background: rgba(139, 92, 246, 0.13); color: #6d28d9; border-color: rgba(139, 92, 246, 0.25); }
.rb-schema--core { background: rgba(14, 165, 233, 0.13); color: #0369a1; border-color: rgba(14, 165, 233, 0.25); }
.rb-schema--mixed { background: rgba(217, 119, 6, 0.14); color: #b45309; border-color: rgba(217, 119, 6, 0.28); }

.rb-operatore {
  display: inline-flex;
  align-items: center;
  gap: 0.3rem;
  font-size: 0.75rem;
  min-width: 0;
}

.rb-operatore i { font-size: 0.68rem; opacity: 0.7; }

.rb-quando-rel { font-size: 0.85rem; }
.rb-quando-abs { font-size: 0.72rem; color: var(--text-color-secondary); }

/* Stato */

.rb-stato--in_attesa { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.rb-stato--in_lavorazione { background: rgba(59, 130, 246, 0.14); color: #1d4ed8; }
.rb-stato--firmato { background: rgba(16, 185, 129, 0.14); color: #047857; }
.rb-stato--rifiutato { background: rgba(239, 68, 68, 0.13); color: #b91c1c; }
.rb-stato--assente { background: rgba(100, 116, 139, 0.14); color: #475569; }
.rb-stato--conflitto { background: rgba(239, 68, 68, 0.18); color: #991b1b; }

.rb-transizione {
  margin-left: 0.4rem;
  color: var(--text-color-secondary);
  font-size: 0.8rem;
}

/* Azioni */

.rb-azioni-sep {
  width: 1px;
  height: 1.35rem;
  margin: 0 0.25rem;
  background: var(--surface-border);
  flex-shrink: 0;
}

.rb-azioni :deep(.rb-azione--ko:enabled:hover) {
  background: rgba(239, 68, 68, 0.1);
  border-color: rgba(239, 68, 68, 0.28);
  color: #b91c1c;
}

.rb-azioni :deep(.rb-azione--warn:enabled:hover) {
  background: rgba(245, 158, 11, 0.14);
  border-color: rgba(245, 158, 11, 0.32);
  color: #b45309;
}

/* Vuoto */
.rb-vuoto {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  padding: 3.5rem 2rem;
  text-align: center;
  color: var(--text-color-secondary);
}

/* ── Dialoghi ──────────────────────────────────────────────────────── */

.rb-conferma-icona--firmato { background: rgba(16, 185, 129, 0.14); color: #047857; }
.rb-conferma-icona--rifiutato { background: rgba(239, 68, 68, 0.12); color: #b91c1c; }

/* ── Verifica contabile ─────────────────────────────────────────────── */

/* Confronto file ↔ contabilità */

.rb-conf-voce {
  display: flex;
  flex-direction: column;
  gap: 0.15rem;
}

.rb-conf-ko { color: #b91c1c; }

.rb-verifica-toggle {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
}

.rb-verifica-toggle label {
  cursor: pointer;
}

/* Spunta di riga: verde se registrato, rosso se no */
.rb-spunta {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 1.6rem;
  height: 1.6rem;
  border-radius: 50%;
  font-size: 0.75rem;
}

.rb-spunta--ok { background: rgba(16, 185, 129, 0.16); color: #047857; }
.rb-spunta--ko { background: rgba(239, 68, 68, 0.14); color: #b91c1c; }

/* Un motivo che nessuno sa di poter leggere e' un motivo che non esiste:
   il cursore e il bordo al passaggio dicono che la casella e' interrogabile */
.rb-spunta--motivo {
  cursor: help;
  box-shadow: inset 0 0 0 1px currentColor;
  transition: transform 0.12s ease, box-shadow 0.12s ease;
}

.rb-spunta--motivo:hover {
  transform: scale(1.12);
  box-shadow: inset 0 0 0 2px currentColor;
}

.rb-nota {
  font-size: 0.8rem;
  color: #b45309;
}

/* Storico */
.rb-evento {
  display: inline-block;
  padding: 0.18rem 0.55rem;
  border-radius: 5px;
  font-size: 0.74rem;
  font-weight: 600;
}

.rb-evento--ok { background: rgba(59, 130, 246, 0.12); color: #1d4ed8; }
.rb-evento--ko { background: rgba(239, 68, 68, 0.12); color: #b91c1c; }

.rb-transizione-testo {
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  font-size: 0.83rem;
}

.rb-transizione-testo i {
  font-size: 0.7rem;
  color: var(--text-color-secondary);
}

.rb-testo-ko { color: #b91c1c; }

/* ══════════════════════════════════════════════════════════════════════
   Sotto i 1100px: ogni riga diventa una scheda.
   PrimeVue nasconde l'intestazione e mette i td in flex; qui si aggiunge
   l'aspetto di scheda e si rendono leggibili le etichette di colonna.
   ══════════════════════════════════════════════════════════════════════ */

@media screen and (max-width: 1100px) {
  .rb-tabella {
    border: 0;
    background: transparent;
    box-shadow: none;
    overflow: visible;
  }

  .rb-dt :deep(.p-datatable-tbody > tr) {
    display: block;
    margin-bottom: 1rem;
    border: 1px solid var(--surface-border);
    border-radius: 10px;
    overflow: hidden;
    background: var(--surface-card);
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
  }

  .rb-dt :deep(.p-datatable-tbody > tr:hover) {
    background: var(--surface-card);
  }

  .rb-dt :deep(.p-datatable-tbody > tr > td) {
    padding: 0.7rem 1rem;
    gap: 1rem;
    border-bottom: 1px solid var(--surface-border) !important;
  }

  .rb-dt :deep(.p-datatable-tbody > tr > td:last-child) {
    border-bottom: 0 !important;
    background: var(--surface-ground);
  }

  .rb-dt :deep(.p-column-title) {
    font-size: 0.7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-color-secondary);
    flex-shrink: 0;
  }

  /* Il nome del file è l'informazione principale: prende tutta la riga */
  .rb-dt :deep(.p-datatable-tbody > tr > td:nth-child(2)) {
    flex-direction: column;
    align-items: flex-start !important;
    gap: 0.35rem;
  }

  .rb-file { width: 100%; }

  .rb-azioni {
    justify-content: flex-end;
    gap: 0.4rem;
    flex-wrap: wrap;
  }

  .rb-azioni :deep(.rb-azione) {
    width: 2.5rem;
    height: 2.5rem;
    border-color: var(--surface-border);
    background: var(--surface-card);
  }

  .rb-vuoto {
    border: 1px solid var(--surface-border);
    border-radius: 10px;
    background: var(--surface-card);
  }

  .rb-dt :deep(.p-paginator) {
    border: 1px solid var(--surface-border);
    border-radius: 10px;
  }
}

/* ══════════════════════════════════════════════════════════════════════
   Tablet
   ══════════════════════════════════════════════════════════════════════ */

@media screen and (max-width: 900px) {
  .rb-header {
    align-items: flex-start;
    flex-direction: column;
    gap: 1rem;
    padding: 1.5rem 1.25rem;
  }

  .rb-title { font-size: 1.35rem; }

  .rb-sync {
    width: 100%;
    justify-content: center;
  }

  .rb-body {
    padding: 1.5rem 1.25rem 2.5rem;
  }

  .rb-banche {
    flex-direction: column;
    align-items: stretch;
    gap: 0.6rem;
  }

  .rb-banche-toggle {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    gap: 0.4rem;
  }

  .rb-banche-toggle :deep(.p-button) {
    min-width: 0;
    width: 100%;
    border-radius: 8px !important;
    border: 1px solid var(--surface-border) !important;
  }

  .rb-filtri-campi {
    grid-template-columns: 1fr;
    padding: 1.25rem;
    gap: 1.15rem;
  }

  .rb-campo--periodo { min-width: 0; }

  .rb-filtri-piede {
    flex-direction: column;
    align-items: stretch;
    padding: 1rem 1.25rem;
  }

  .rb-riepilogo {
    justify-content: flex-start;
    gap: 0.9rem;
    flex-wrap: wrap;
  }

  .rb-filtri-azioni { width: 100%; }

  .rb-filtri-azioni :deep(.p-button) {
    flex: 1;
    justify-content: center;
  }

  .rb-dialog :deep(.p-dialog) {
    width: 94vw !important;
    max-width: 94vw;
    margin: 0;
  }
}

/* ══════════════════════════════════════════════════════════════════════
   Telefono: aree toccabili più grandi e niente colonne affiancate
   ══════════════════════════════════════════════════════════════════════ */

@media screen and (max-width: 600px) {
  .rb-header {
    padding: 1.25rem 1rem;
  }

  .rb-header-icon {
    width: 2.5rem;
    height: 2.5rem;
  }

  .rb-header-icon i { font-size: 1.15rem; }

  .rb-title { font-size: 1.2rem; }
  .rb-subtitle { font-size: 0.82rem; }

  .rb-body { padding: 1.25rem 1rem 2rem; }

  .rb-banche-toggle {
    grid-template-columns: 1fr 1fr;
  }

  .rb-periodo {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .rb-periodo-sep { display: none; }

  /* Etichetta e valore restano affiancati: impilarli raddoppia l'altezza
     della scheda, e i valori qui sono corti (badge, date, importi). */
  .rb-dt :deep(.p-datatable-tbody > tr > td) {
    padding: 0.7rem 0.9rem;
    gap: 0.75rem;
  }

  .rb-cella-multi {
    align-items: flex-end;
    text-align: right;
  }

  .rb-azioni :deep(.rb-azione) {
    width: 2.75rem;
    height: 2.75rem;
  }

  .rb-azioni :deep(.rb-azione .p-button-icon) { font-size: 1.05rem; }

  .rb-dt :deep(.p-paginator) {
    justify-content: center;
    padding: 0.75rem 0.5rem;
  }

  .rb-dt :deep(.p-paginator .p-paginator-current) {
    width: 100%;
    text-align: center;
    order: -1;
  }

  
  }
</style>
